import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { httpGetMines, httpGetAllMineShafts } from 'services/mines';
import './Dashboard.scss';

const MINES_CACHE_KEY = 'mineShaftSelector_mines';
const SHAFTS_CACHE_KEY = 'mineShaftSelector_shafts';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

export default function MineShaftSelector({ onMineSelect, onShaftSelect, selectedMineId, selectedShaftId }) {
  const { t } = useTranslation();
  const [mineDropdownOpen, setMineDropdownOpen] = useState(false);
  const [shaftDropdownOpen, setShaftDropdownOpen] = useState(false);
  const [mines, setMines] = useState([]);
  const [allShafts, setAllShafts] = useState([]);
  const [filteredShafts, setFilteredShafts] = useState([]);
  const [selectedMine, setSelectedMine] = useState(null);
  const [selectedShaft, setSelectedShaft] = useState(null);

  // Fetch mines and shafts on mount with caching
  useEffect(() => {
    const fetchData = async () => {
      try {
        
        // Try to load from cache first
        const cachedMines = sessionStorage.getItem(MINES_CACHE_KEY);
        const cachedShafts = sessionStorage.getItem(SHAFTS_CACHE_KEY);
        
        let minesData = null;
        let shaftsData = null;
        
        // Check if cache is valid
        if (cachedMines && cachedShafts) {
          try {
            const minesCache = JSON.parse(cachedMines);
            const shaftsCache = JSON.parse(cachedShafts);
            const now = Date.now();
            
            if (now - minesCache.timestamp < CACHE_DURATION && 
                now - shaftsCache.timestamp < CACHE_DURATION) {
              // Use cached data
              minesData = minesCache.data;
              shaftsData = shaftsCache.data;
              console.log('MineShaftSelector: Using cached data');
            }
          } catch (e) {
            console.warn('MineShaftSelector: Cache parse error', e);
          }
        }
        
        // Fetch from API if no valid cache
        if (!minesData || !shaftsData) {
          console.log('MineShaftSelector: Fetching fresh data from API');
          
          // Fetch all mines
          const minesResponse = await httpGetMines();
          minesData = Array.isArray(minesResponse.data) ? minesResponse.data : minesResponse.data.results || [];
          
          // Fetch ALL shafts at once (single API call!)
          const shaftsResponse = await httpGetAllMineShafts();
          shaftsData = Array.isArray(shaftsResponse.data) ? shaftsResponse.data : shaftsResponse.data.results || [];
          
          // Cache the data
          sessionStorage.setItem(MINES_CACHE_KEY, JSON.stringify({
            data: minesData,
            timestamp: Date.now()
          }));
          sessionStorage.setItem(SHAFTS_CACHE_KEY, JSON.stringify({
            data: shaftsData,
            timestamp: Date.now()
          }));
        }
        
        setMines(minesData);
        setAllShafts(shaftsData);
        setFilteredShafts(shaftsData); // Initially show all shafts
      } catch (err) {
        console.error('Error fetching mines/shafts:', err);
      }
    };

    fetchData();
  }, []);

  // Update selected mine from prop
  useEffect(() => {
    if (mines.length > 0 && selectedMineId) {
      const mine = mines.find(m => m.id === selectedMineId);
      if (mine) {
        setSelectedMine(mine);
        // Filter shafts by mine (handle both object and ID)
        const shaftsForMine = allShafts.filter(shaft => {
          const shaftMineId = typeof shaft.mine === 'object' ? shaft.mine?.id : shaft.mine;
          return shaftMineId === mine.id;
        });
        setFilteredShafts(shaftsForMine);
      }
    } else if (!selectedMineId) {
      setSelectedMine(null);
      setFilteredShafts(allShafts);
    }
  }, [mines, allShafts, selectedMineId]);

  // Update selected shaft from prop
  useEffect(() => {
    if (filteredShafts.length > 0 && selectedShaftId) {
      const shaft = filteredShafts.find(s => s.id === selectedShaftId);
      if (shaft) {
        setSelectedShaft(shaft);
      }
    } else if (!selectedShaftId) {
      setSelectedShaft(null);
    }
  }, [filteredShafts, selectedShaftId]);

  const toggleMine = () => setMineDropdownOpen(prev => !prev);
  const toggleShaft = () => setShaftDropdownOpen(prev => !prev);

  const handleMineSelect = (mine) => {
    console.log('MineShaftSelector: Mine selected:', mine);
    console.log('MineShaftSelector: All shafts:', allShafts);
    
    // Close dropdown immediately
    setMineDropdownOpen(false);
    
    setSelectedMine(mine);
    
    // Filter shafts or show all
    if (mine) {
      // Check if shaft.mine is an object or ID
      const shaftsForMine = allShafts.filter(shaft => {
        const shaftMineId = typeof shaft.mine === 'object' ? shaft.mine?.id : shaft.mine;
        console.log('MineShaftSelector: Comparing shaft.mine:', shaftMineId, 'with mine.id:', mine.id);
        return shaftMineId === mine.id;
      });
      console.log('MineShaftSelector: Filtered shafts:', shaftsForMine);
      setFilteredShafts(shaftsForMine);
      // Reset shaft selection when mine changes
      setSelectedShaft(null);
      if (onShaftSelect) onShaftSelect(null);
    } else {
      setFilteredShafts(allShafts);
      setSelectedShaft(null);
      if (onShaftSelect) onShaftSelect(null);
    }
    
    if (onMineSelect) {
      onMineSelect(mine);
    }
  };

  const handleShaftSelect = (shaft) => {
    // Close dropdown immediately
    setShaftDropdownOpen(false);
    
    setSelectedShaft(shaft);
    
    if (onShaftSelect) {
      onShaftSelect(shaft);
    }
  };

  return (
    <div className="mine-shaft-selector d-flex align-items-center">
      {/* Mine Selector - only show if more than one mine */}
      {mines.length > 1 && (
        <>
          <i className="fas fa-mountain mr-2" />
          <Dropdown isOpen={mineDropdownOpen} toggle={toggleMine} className="mine-dropdown mr-3">
            <DropdownToggle caret color="link" className="p-0 text-dark">
              {selectedMine ? selectedMine.name : t('mine_all_mines')}
            </DropdownToggle>
            <DropdownMenu>
              <DropdownItem toggle={false} onClick={() => handleMineSelect(null)}>
                {t('mine_all_mines')}
              </DropdownItem>
              {mines.map(mine => (
                <DropdownItem key={mine.id} toggle={false} onClick={() => handleMineSelect(mine)}>
                  {mine.name}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        </>
      )}

      {/* Shaft Selector */}
      <i className="fas fa-layer-group mr-2" />
      <Dropdown isOpen={shaftDropdownOpen} toggle={toggleShaft} className="shaft-dropdown">
        <DropdownToggle caret color="link" className="p-0 text-dark">
          {selectedShaft ? selectedShaft.name : t('mine_all_shafts')}
        </DropdownToggle>
        <DropdownMenu>
          <DropdownItem toggle={false} onClick={() => handleShaftSelect(null)}>
            {t('mine_all_shafts')}
          </DropdownItem>
          {filteredShafts.map(shaft => (
            <DropdownItem key={shaft.id} toggle={false} onClick={() => handleShaftSelect(shaft)}>
              {shaft.name}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
    </div>
  );
}

